import React, {useState, useEffect, useCallback} from "react"
import {TextArea} from "@irati/ui/src/FormItem"
import {InjectIntl, injectIntl} from "@irati/utils/lang"
import {EMOJI} from "@irati/ui/src/Reactions/constants"
import {orderBy} from "lodash"
import styled from "styled-components"
import OccasionsService from "@irati/recognition/services/Occasions"
import {Occasion} from "@irati/types"
import OccasionIcon from "@irati-web/recognition/src/components/shared/OccasionIcon"
import Flex from "@irati/ui/src/Flex"
import mainTheme from "@irati/ui/src/global/theme"
import CompanyValueTag from "@irati-web/companyvalues/src/components/CompanyValuesList/Tag"
import {CompanyValue} from "@irati/companyvalues/types"
import {CompanyValuesSelectExtraAction as CompanyValuesSelect} from "@irati-web/fields/companyValues"
import OccasionsSelectExtraAction from "@irati-web/fields/occasions/OccasionsSelectExtraAction"
import {recognitionSchema} from "@irati-web/fields/poll/votes/validation"
import {MAX_CHAR_LENGTH} from "@irati-web/fields/poll/votes/validation/recognition"
export interface OnChangeProps {
  message: string
  companyValues: CompanyValue[]
  occasion: Occasion
}
interface Props extends InjectIntl {
  onChange: (props: OnChangeProps) => void
  name: string
  defaultValues?: OnChangeProps
  label?: string
  placeholder?: string
  validation?: object
  messageLength?: number
}
const StyledTextArea = styled(TextArea)`
  textarea {
    resize: vertical;
    min-height: 40px;
  }
`
const Message: React.FC<Props> = (props) => {
  const {validation, messageLength, intl, onChange, defaultValues} = props
  const [message, setMessage] = useState<string>(defaultValues?.message)
  const [occasionsItems, setOccasions] = useState<Occasion[]>([])
  const [selectedOccasion, setSelectedOccasion] = useState<Occasion>(defaultValues?.occasion)
  const [selectedCompanyValues, setSelectedCompanyValues] = useState<CompanyValue[]>(defaultValues?.companyValues || [])
  useEffect(() => {
    OccasionsService.getAll().then((occasions) => {
      const sortedOccasions = orderBy(occasions, "createdAt", "desc").filter((occasion: Occasion) => !occasion.archived)
      setOccasions(sortedOccasions)
    })
  }, [])
  useEffect(() => {
    onChange({message, occasion: selectedOccasion, companyValues: selectedCompanyValues})
  }, [message, selectedOccasion, selectedCompanyValues])
  const handleChange = (value: string) => {
    setMessage(value)
  }
  const onOccasionSelect = (occasionUUID: string) => {
    const occasion = occasionsItems.find(({uuid}) => uuid === occasionUUID)
    if (!occasion) {
      return
    }
    setSelectedOccasion(occasion)
  }
  const onRemoveCompanyValue = (uuidToRemove: string) => {
    const newCompanyValues = selectedCompanyValues.filter(({uuid}) => uuid !== uuidToRemove)
    setSelectedCompanyValues(newCompanyValues)
  }
  const onCompanyValueSelect = (newSelectedCompanyValues: CompanyValue[]) => {
    setSelectedCompanyValues(newSelectedCompanyValues)
  }
  const ExtraActions = () => {
    return (
      <Flex>
        <OccasionsSelectExtraAction occasionId={selectedOccasion?.uuid} onChange={onOccasionSelect} />
        <CompanyValuesSelect companyValues={selectedCompanyValues} onChange={onCompanyValueSelect} />
      </Flex>
    )
  }
  const onRemoveOccasion = () => {
    setSelectedOccasion(undefined)
  }
  const ExtraContent = useCallback(() => {
    return (
      <Flex gap={mainTheme.spacing.half}>
        {selectedOccasion && (
          <OccasionIcon onDelete={onRemoveOccasion} occasion={selectedOccasion} key={selectedOccasion.uuid}></OccasionIcon>
        )}
        {selectedCompanyValues?.map((companyValue) => (
          <CompanyValueTag
            onDelete={() => onRemoveCompanyValue(companyValue.uuid)}
            companyValue={companyValue}
            key={companyValue.uuid}></CompanyValueTag>
        ))}
      </Flex>
    )
  }, [selectedCompanyValues, selectedOccasion])
  const hasExtraContent = selectedOccasion || selectedCompanyValues?.length
  return (
    <StyledTextArea
      rows={4}
      {...props}
      flush
      name="message"
      id="message"
      validation={validation || recognitionSchema(intl)}
      onChange={handleChange}
      textReplaceTypes={[EMOJI]}
      value={message}
      maxLength={messageLength || MAX_CHAR_LENGTH}
      ExtraActions={ExtraActions}
      ExtraContent={hasExtraContent ? ExtraContent : undefined}
    />
  )
}

export default injectIntl(Message)
